import { GENERATION_TOOL } from 'src/data/generationTool';
import { defineDefaultEnumValue, defineEnumClampFunction } from 'src/lib/enum';
import * as env from 'src/lib/env';

export const INFINITY_EXTENSION_STORE_URL = `https://chrome.google.com/webstore/detail/${env.getInfinityExtensionID()}`;

/**
 * Messages that Extension sends to the "embedded" Platform
 */
export const INCOMING_MESSAGE = {
  displayGenerationForm: 'displayGenerationForm',
  displayInsightsPanel: 'displayInsightsPanel',
  displaySuggestionsPanel: 'displaySuggestionsPanel',
  displayImportBrief: 'displayImportBrief',
  displaySettings: 'displaySettings',
  scoreText: 'scoreText',
  login: 'login',
  logout: 'logout',
  activateWorkspace: 'activateWorkspace',
  detectPageAssets: 'detectPageAssets',
  createProjectFromAssets: 'createProjectFromAssets',
  createVariationFromAsset: 'createVariationFromAsset',

  // deprecated - starting from Infinity 0.0.42, these are not needed
  windowFocused: 'windowFocused',
  platformInfoCheck: 'platformInfoCheck',
  // deprecated - starting from from Infinity 0.0.44.1 we track events through BG script
  trackEvent: 'trackEvent',
  // deprecated - starting from Infinity 0.0.50.0, extension does not support "variations generation" flow
  displayVariationsForm: 'displayVariationsForm',
} as const;
export type IncomingMessageType = (typeof INCOMING_MESSAGE)[keyof typeof INCOMING_MESSAGE];

/**
 * Messages that "embedded" Platform sends to the Extension
 */
export const OUTGOING_MESSAGE = {
  platformReady: 'platformReady',
  frameViewReady: 'frameViewReady',
  closeFrame: 'closeFrame',
  submitInstructions: 'submitInstructions',
  displayFullscreen: 'displayFullscreen',
  scoreTextResult: 'scoreTextResult',
  displaySuggestionInsights: 'displaySuggestionInsights',
  applySuggestion: 'applySuggestion',
  classifyTextResult: 'classifyTextResult',
  manualToolSelected: 'manualToolSelected',
  dictionaryBriefs: 'dictionaryBriefs',
  dictionaryResources: 'dictionaryResources',
  importedBrief: 'importedBrief',
  saveSettings: 'saveSettings',
  detectPageAssetsResult: 'detectPageAssetsResult',
  createProjectFromAssetsResult: 'createProjectFromAssetsResult',
  createVariationFromAssetResult: 'createVariationFromAssetResult',

  // deprecated - starting from Infinity 0.0.42, extension gets "platform info" and "workspaces" from BG script
  platformInfo: 'platformInfo',
  dictionaryWorkspaces: 'dictionaryWorkspaces',
  // deprecated - starting from Infinity 0.0.50.0, extension does not support "variations generation" flow
  switchVariationsGenerationVariant: 'switchVariationsGenerationVariant',
  displayVariationInsights: 'displayVariationInsights',
} as const;
export type OutgoingMessageType = (typeof OUTGOING_MESSAGE)[keyof typeof OUTGOING_MESSAGE];

/**
 * Messages that "external" Platform sends to the Extension
 */
export const EXTERNAL_MESSAGE = {
  login: 'login',
  logout: 'logout',
  settingsChanged: 'settingsChanged',

  // deprecated - starting from Infinity 0.0.50.0, extension uses "User.settings" for customer/workspace selection
  // activateWorkspace: 'activateWorkspace',
} as const;
export type ExternalMessageType = (typeof EXTERNAL_MESSAGE)[keyof typeof EXTERNAL_MESSAGE];

export const ERROR_CODE = {
  quotaEnded: 'quota_ended',
} as const;
export type ErrorCode = (typeof ERROR_CODE)[keyof typeof ERROR_CODE];

export const DEFAULT_INFINITY_GENERATION_TOOL = GENERATION_TOOL.basicContentImprover;

/**
 * Infinity user flow type
 */
export const INFINITY_USER_FLOW = {
  selectionText: 'selection_text',
  attachedInput: 'attached_input',
} as const;
export type InfinityUserFlow = (typeof INFINITY_USER_FLOW)[keyof typeof INFINITY_USER_FLOW];

/**
 * Website host type for Infinity extension
 * This enum should be synced with HOST_TYPE in Infinity extension repo
 */
export const INFINITY_HOST_TYPE = {
  unknown: 'unknown',
  anywordPlatform: 'anywordPlatform',
  chatgpt: 'chatgpt',
  copilot: 'copilot',
  canva: 'canva',
  claude: 'claude',
  gemini: 'gemini',
  glean: 'glean',
  notion: 'notion',
  google: 'google',
  googleAds: 'googleAds',
  googleDocs: 'googleDocs',
  googleSheets: 'googleSheets',
  googleSlides: 'googleSlides',
  wordOnline: 'wordOnline',
  gmail: 'gmail',
  facebook: 'facebook',
  metaAds: 'metaAds',
  instagram: 'instagram',
  twitter: 'twitter',
  twitterAds: 'twitterAds',
  youtube: 'youtube',
  linkedIn: 'linkedIn',
  linkedInAds: 'linkedInAds',
  figma: 'figma',
  hubspot: 'hubspot',
  sprinklr: 'sprinklr',
} as const;
export type InfinityHostType = (typeof INFINITY_HOST_TYPE)[keyof typeof INFINITY_HOST_TYPE];
defineDefaultEnumValue(INFINITY_HOST_TYPE, INFINITY_HOST_TYPE.unknown);
defineEnumClampFunction(INFINITY_HOST_TYPE, INFINITY_HOST_TYPE.unknown);

export const INFINITY_SUGGESTION_MODE = {
  improveScore: 'improve_score',
  applyBrandVoice: 'apply_brand_voice',
} as const;
export type InfinitySuggestionMode =
  (typeof INFINITY_SUGGESTION_MODE)[keyof typeof INFINITY_SUGGESTION_MODE];

export const INFINITY_SUGGESTION_MODE_DEFAULT = INFINITY_SUGGESTION_MODE.improveScore;

export const CLASSIFICATION_CONFIDENCE_THRESHOLD = 0.8;

/**
 * Sits alongside `LANGUAGE` values in the panel's settings, but is never sent to the backend —
 * it is resolved to a real language right before generation.
 */
export const INFINITY_OUTPUT_LANGUAGE_AUTO = 'auto';
export type InfinityOutputLanguageAuto = typeof INFINITY_OUTPUT_LANGUAGE_AUTO;
